import { Card, ExerciseThumbnail } from "../../design-v2";
import InfoHelp from "./InfoHelp";
import {
  formatIntensity,
  formatRepPrescription,
  resolveDisplayIntensity,
} from "./presentationFormatters";

function joinClassNames(...values) {
  return values.filter(Boolean).join(" ");
}

function formatLaneBadge(label) {
  const laneNumber = Number(String(label || "").match(/(\d+)$/)?.[1]);

  if (Number.isInteger(laneNumber) && laneNumber >= 1 && laneNumber <= 26) {
    return String.fromCharCode(64 + laneNumber);
  }

  return label;
}

const RIR_HELP = "Reps in Reserve estimates how many more good reps you could perform before failure. It helps prescribe effort without requiring every set to reach failure.";

function getPrescriptionRows(sets = [], defaultIntensity, presentationFallbackIntensity) {
  const rows = sets.map((set, index) => ({
    id: set.id || index,
    label: `Set ${index + 1}`,
    prescription: formatRepPrescription(set.display),
    repMode: set.display?.repMode || "reps",
    resolvedIntensity: resolveDisplayIntensity(
      set.display?.intensity,
      defaultIntensity,
      set.display?.repMode === "seconds"
        ? null
        : presentationFallbackIntensity
    ),
  }));

  return rows.map((row) => ({
    ...row,
    intensity: formatIntensity(row.resolvedIntensity),
  }));
}

function getSharedRepPrescription(rows) {
  if (rows.length === 0 || !rows[0].prescription) {
    return null;
  }

  const first = rows[0];
  const allMatch = rows.every(
    (row) =>
      row.prescription === first.prescription &&
      row.repMode === first.repMode
  );

  return allMatch ? first : null;
}

function getSharedIntensity(rows) {
  if (rows.length === 0 || !rows[0].intensity) {
    return null;
  }

  const first = rows[0];
  return rows.every((row) => row.intensity === first.intensity) ? first : null;
}

function IntensityHelp({ intensity, compact = false }) {
  if (intensity?.mode !== "rir") {
    return null;
  }

  return (
    <InfoHelp label="RIR" title="Reps in Reserve" tone={compact ? "subtle" : "default"}>
      {RIR_HELP}
    </InfoHelp>
  );
}

export default function ExerciseCard({
  block,
  position,
  contained = false,
  presentationFallbackIntensity = null,
}) {
  const name = block.exercise || block.name || "Unnamed exercise";
  const sets = Array.isArray(block.sets) ? block.sets : [];
  const rows = getPrescriptionRows(
    sets,
    block.display?.intensity,
    presentationFallbackIntensity
  );
  const sharedRepPrescription = getSharedRepPrescription(rows);
  const sharedIntensity = getSharedIntensity(rows);
  const detailedRows = rows
    .map((row) => ({
      ...row,
      showPrescription: !sharedRepPrescription && Boolean(row.prescription),
      showIntensity: !sharedIntensity && Boolean(row.intensity),
    }))
    .filter((row) => row.showPrescription || row.showIntensity);
  const Wrapper = contained ? "article" : Card;
  const wrapperProps = contained ? {} : { as: "article", padding: "none" };

  return (
    <Wrapper
      {...wrapperProps}
      className={joinClassNames(
        "lz-product-exercise-card",
        contained && "lz-product-exercise-card--contained"
      )}
      aria-label={name}
      data-layout="thumbnail-first"
    >
      <header className="lz-product-exercise-card__header">
        {position ? <span className="lz-product-block-index">{position}</span> : null}
        <span className="lz-product-exercise-card__media" data-testid="exercise-media">
          <ExerciseThumbnail
            exerciseId={block.exerciseId}
            src={null}
            size={104}
            alt=""
          />
          {block.label ? (
            <span className="lz-product-exercise-card__lane-badge">
              {formatLaneBadge(block.label)}
            </span>
          ) : null}
        </span>
        <div className="lz-product-exercise-card__heading-copy">
          <h3 className="lz-product-exercise-card__name">{name}</h3>
        </div>
      </header>

      <dl className="lz-product-exercise-card__stats">
        <div>
          <dt>Sets</dt>
          <dd>{sets.length}</dd>
        </div>
        {sharedRepPrescription ? (
          <div>
            <dt>{sharedRepPrescription.repMode === "seconds" ? "Time" : "Reps"}</dt>
            <dd>
              {sharedRepPrescription.repMode === "seconds"
                ? sharedRepPrescription.prescription
                : sharedRepPrescription.prescription.replace(/ reps$/, "")}
            </dd>
          </div>
        ) : null}
        {block.tempo ? (
          <div>
            <dt>Tempo</dt>
            <dd>{block.tempo}</dd>
          </div>
        ) : null}
        {block.rest ? (
          <div>
            <dt>Rest</dt>
            <dd>{block.rest}</dd>
          </div>
        ) : null}
        {sharedIntensity ? (
          <div>
            <dt className="lz-product-exercise-card__intensity-label">
              {sharedIntensity.resolvedIntensity.mode.toUpperCase()}
              <IntensityHelp intensity={sharedIntensity.resolvedIntensity} compact />
            </dt>
            <dd>{sharedIntensity.resolvedIntensity.value}</dd>
          </div>
        ) : null}
      </dl>

      {detailedRows.length > 0 ? (
        <div className="lz-product-prescriptions" aria-label={`${name} set prescriptions`}>
          {detailedRows.map((row) => (
            <div className="lz-product-prescriptions__row" key={row.id}>
              <span className="lz-product-prescriptions__set">{row.label}</span>
              {row.showPrescription ? (
                <span className="lz-product-prescriptions__reps">{row.prescription}</span>
              ) : null}
              {row.showIntensity ? (
                <span className="lz-product-prescriptions__intensity">
                  {row.intensity}
                  <IntensityHelp intensity={row.resolvedIntensity} compact />
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {block.notes ? <p className="lz-product-block-notes">{block.notes}</p> : null}
    </Wrapper>
  );
}
